import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  getWhatsAppConversations,
  getWhatsAppConversationDetail,
  getWhatsAppMessages,
  markWhatsAppConversationRead,
  sendWhatsAppMessage,
  getWhatsAppContactDetails,
  reactToWhatsAppMessage,
} from '../api';

export const handleWhatsAppError = (error) => {
  const canceled =
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError';
  if (canceled) return;

  if (error?.response) {
    const status = error.response.status;
    if (status >= 500) {
      toast.error('Something went wrong. Please try again later.');
    } else if (status >= 400 && status < 500) {
      const msg =
        error.response.data?.message ||
        error.response.data?.error ||
        'A client error occurred.';
      toast.error(msg);
    }
  } else {
    // Same id as the axios interceptor toast, so one network failure
    // replaces "Something went wrong. Please retry." instead of stacking.
    toast.error('Network error. Please check your connection.', {
      id: 'network-error',
    });
  }
};

export function useWhatsAppConversations(companyId, params = {}) {
  return useQuery({
    queryKey: ['whatsapp-conversations', companyId, params],
    queryFn: () => getWhatsAppConversations(companyId, params),
    enabled: !!companyId,
  });
}

export function useInfiniteWhatsAppConversations(
  companyId,
  params = {},
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ['whatsapp-conversations-infinite', companyId, params],
    queryFn: ({ pageParam = 1 }) =>
      getWhatsAppConversations(companyId, { ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      try {
        const url = new URL(lastPage.next);
        return url.searchParams.get('page');
      } catch {
        return undefined;
      }
    },
    enabled: !!companyId && enabled,
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: enabled ? 20000 : false, // 20 seconds polling when active
  });
}

export function useWhatsAppConversationDetail(
  companyId,
  conversationId,
  enabled = true,
) {
  return useQuery({
    queryKey: ['whatsapp-conversation-detail', companyId, conversationId],
    queryFn: () => getWhatsAppConversationDetail({ companyId, conversationId }),
    enabled: !!companyId && !!conversationId && enabled,
  });
}

export function useWhatsAppMessages(companyId, conversationId, params = {}) {
  return useInfiniteQuery({
    queryKey: ['whatsapp-messages', companyId, conversationId, params],
    queryFn: ({ pageParam }) =>
      getWhatsAppMessages({ companyId, conversationId, params, pageParam }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      try {
        const url = new URL(lastPage.next);
        return url.searchParams.get('page');
      } catch {
        return undefined;
      }
    },
    enabled: !!companyId && !!conversationId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useCustomWhatsAppMessages(
  companyId,
  conversationId,
  params = {},
  enabled = true,
) {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isFetchingOlder, setIsFetchingOlder] = useState(false);
  const [isFetchingNewer, setIsFetchingNewer] = useState(false);

  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasOlder, setHasOlder] = useState(false);

  // We use refs to avoid dependency cycles in callbacks
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Extract page from URL
  const getPageParam = (urlStr) => {
    if (!urlStr) return null;
    try {
      const url = new URL(urlStr);
      return url.searchParams.get('page');
    } catch {
      return null;
    }
  };

  // Function to fetch a specific page and merge
  const fetchPage = async (pageParam, mergeStrategy = 'append_newer') => {
    if (!enabled || !companyId || !conversationId) return null;
    try {
      const data = await getWhatsAppMessages({
        companyId,
        conversationId,
        params,
        pageParam,
      });

      const newRawGroups = data.results || [];
      // Flatmap all messages from this response
      let incomingMessages = [];
      newRawGroups.forEach((group) => {
        if (group.messages) {
          incomingMessages = [...incomingMessages, ...group.messages];
        }
      });

      setMessages((prev) => {
        // Prevent duplicates
        const existingIds = new Set(prev.map((m) => m.id));
        const uniqueIncoming = incomingMessages.filter(
          (m) => !existingIds.has(m.id),
        );

        if (mergeStrategy === 'initial') {
          return incomingMessages;
        } else if (mergeStrategy === 'prepend_older') {
          return [...prev, ...uniqueIncoming];
        } else if (mergeStrategy === 'append_newer') {
          return [...uniqueIncoming, ...prev];
        }
        return prev;
      });

      return data;
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      handleWhatsAppError(error);
      return null;
    }
  };

  // 1. Initial Load
  useEffect(() => {
    let isMounted = true;

    if (enabled && companyId && conversationId) {
      const loadInitial = async () => {
        setIsLoadingInitial(true);
        setMessages([]); // Reset state
        setNextPageUrl(null);
        setHasOlder(false);

        // Fire both GET messages and POST read simultaneously
        const [data] = await Promise.all([
          fetchPage(null, 'initial'),
          markWhatsAppConversationRead({ companyId, conversationId }).catch(
            console.error,
          ),
        ]);

        // Invalidate sidebar to update unread counts
        queryClient.invalidateQueries({
          queryKey: ['whatsapp-conversations', companyId],
        });

        if (isMounted && data) {
          if (data.next) {
            setNextPageUrl(data.next);
            setHasOlder(true);
          }
        }
        if (isMounted) setIsLoadingInitial(false);
      };
      loadInitial();
    } else {
      setMessages([]);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, conversationId, enabled]);

  // Polling for Newer Messages (Right side)
  const pollTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !companyId || !conversationId) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      return;
    }

    const poll = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const msgs = messagesRef.current;
        let sinceId = undefined;
        if (msgs && msgs.length > 0) {
          const maxId = Math.max(
            ...msgs.map((m) => parseInt(m.id, 10)).filter((id) => !isNaN(id)),
          );
          if (maxId > -Infinity) {
            sinceId = maxId;
          }
        }

        const params = { flat: true, order: 'asc' };
        if (sinceId !== undefined) {
          params.since_id = sinceId;
        }

        const data = await getWhatsAppMessages({
          companyId,
          conversationId,
          params,
          signal: controller.signal,
        });

        // Backend might return raw array or { results: [...] } depending on pagination
        let incomingMessages = Array.isArray(data) ? data : data.results || [];

        if (incomingMessages.length > 0) {
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => String(m.id)));
            const uniqueIncoming = incomingMessages.filter(
              (m) => !existingIds.has(String(m.id)),
            );

            if (uniqueIncoming.length === 0) return prev;

            // Backend returns order=asc (oldest first).
            // Our UI expects rawMessages in descending order (newest first).
            const reversedIncoming = [...uniqueIncoming].reverse();
            return [...reversedIncoming, ...prev];
          });

          // Mark new messages as read
          markWhatsAppConversationRead({ companyId, conversationId }).catch(
            console.error,
          );
          queryClient.invalidateQueries({
            queryKey: ['whatsapp-conversations', companyId],
          });
          queryClient.invalidateQueries({
            queryKey: [
              'whatsapp-conversation-detail',
              companyId,
              conversationId,
            ],
          });
        }
      } catch (error) {
        if (
          error.code === 'ERR_CANCELED' ||
          error.name === 'CanceledError' ||
          error.name === 'AbortError'
        ) {
          return; // Expected cancellation
        }
        console.error('Error polling messages:', error);
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    };

    // Run polling every 15 seconds
    pollTimerRef.current = setInterval(poll, 15000);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [companyId, conversationId, enabled, queryClient]);

  // 2. Fetch Older Messages (Scroll up)
  const fetchOlderMessages = useCallback(async () => {
    if (!hasOlder || isFetchingOlder || !nextPageUrl) return;

    setIsFetchingOlder(true);
    const pageParam = getPageParam(nextPageUrl);
    const data = await fetchPage(pageParam, 'prepend_older');

    if (data) {
      setNextPageUrl(data.next);
      setHasOlder(!!data.next);
    }
    setIsFetchingOlder(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOlder, isFetchingOlder, nextPageUrl, companyId, conversationId]);

  // 3. Fetch Newer Messages (Refresh / Poll)
  const fetchNewerMessages = useCallback(async () => {
    if (isFetchingNewer) return;
    setIsFetchingNewer(true);
    // Page 1 always has the latest messages
    // Fire read API concurrently to mark new fetched messages as read
    await Promise.all([
      fetchPage(null, 'append_newer'),
      markWhatsAppConversationRead({ companyId, conversationId }).catch(
        console.error,
      ),
    ]);
    queryClient.invalidateQueries({
      queryKey: ['whatsapp-conversations', companyId],
    });
    queryClient.invalidateQueries({
      queryKey: ['whatsapp-conversation-detail', companyId, conversationId],
    });
    setIsFetchingNewer(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingNewer, companyId, conversationId]);

  return {
    messages,
    isLoadingInitial,
    isFetchingOlder,
    isFetchingNewer,
    fetchOlderMessages,
    fetchNewerMessages,
    hasOlder,
  };
}

export function useMarkWhatsAppConversationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markWhatsAppConversationRead,
    onSuccess: (_, variables) => {
      // Invalidate the conversations list and the specific conversation detail
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-conversations', variables.companyId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          'whatsapp-conversation-detail',
          variables.companyId,
          variables.conversationId,
        ],
      });
    },
    onError: (error) => {
      handleWhatsAppError(error);
    },
  });
}

export function useSendWhatsAppMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendWhatsAppMessage,
    onSuccess: (_, variables) => {
      // Invalidate messages list to reflect the newly sent message immediately
      // Actually we need conversationId to invalidate messages.
      // But sendWhatsAppMessage doesn't explicitly take conversationId in the URL according to the API (it uses to_phone).
      // However, we can invalidate all messages for this company, or we could require the caller to pass conversationId in variables to help invalidation.
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-messages', variables.companyId],
      });
    },
    onError: (error) => {
      handleWhatsAppError(error);
    },
  });
}

export function useWhatsAppContactDetails(companyId, clientId) {
  return useQuery({
    queryKey: ['whatsapp-contact-details', companyId, clientId],
    queryFn: () => getWhatsAppContactDetails({ companyId, clientId }),
    enabled: !!companyId && !!clientId,
  });
}

export function useReactToWhatsAppMessage() {
  return useMutation({
    mutationFn: reactToWhatsAppMessage,
    onError: (error) => {
      handleWhatsAppError(error);
    },
  });
}
